import { Button } from "@/design-system/components/ui/button";
import { CalendarDays, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Clinic } from "@fysaro/domain";
import { PublicClinicTeamList } from "./PublicClinicTeamList";
import { useCreateBookingSession } from "./hooks/useCreateBookingSession";
import { redirectToApp } from "@/utils/appUrl";

// 🛠️ Cambiamos el nombre para que quede claro que es la vista pública del paciente
export function PublicClinicTeam({ clinic }: { clinic: Clinic }) {
  const { t } = useTranslation(["clinic", "booking-patient"]);
  const createSession = useCreateBookingSession();

  // 🚀 Convertimos el staff del repo de Prisma al array de profesionales que espera tu ClinicTeamList
  const listProfessionals =
    (clinic as Clinic)?.professionals
      .map((s: any) => ({
        id: s.id,
        name: s.name || "Profesional",
        imageUrl: s.imageUrl,
        specialty:
          s.specialty?.name ||
          t("clinic:tabs.team.specialist", "Especialista"),
        specialtyColor: s.specialty?.color ?? null,
      })) || [];

  // Acción de reserva rápida en la clí­nica actual
  const handleQuickBook = async () => {
    try {
      // Generamos la sesión puente en la BD de forma así­ncrona
      const response = await createSession.mutateAsync({
        clinicId: clinic.id,
        source: "DIRECT_LINK", // ðŸŽ¯ Indicamos que es un enlace directo sin servicio
      });

      // Viajamos limpios usando el sessionId recién creado
      const targetPath = `/patient/bookings/landing?sessionId=${response.id}`;
      redirectToApp(targetPath);
    } catch (error) {
      console.error("Error al crear la sesión puente de reserva", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* âš¡ CABECERA ORIENTADA A CONVERSIÓN DE CITAS */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-background border border-border/60 p-5 md:p-6 rounded-2xl shadow-sm text-left">
        <div className="space-y-1">
          <h3 className="text-lg md:text-xl font-black tracking-tight text-foreground">
            {t("clinic:tabs.team.name", "Nuestro Equipo Médico")}
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
            {t("clinic:tabs.team_desc", { name: clinic.name })}
          </p>
        </div>

        {/* ðŸŽ¯ CTA Público: En lugar de "Asignar personal", invitamos a reservar */}
        <Button
          onClick={handleQuickBook}
          disabled={createSession.isPending}
          className="w-full sm:w-auto rounded-xl shadow-md shadow-primary/10 gap-2 font-bold text-xs uppercase tracking-wider h-11 sm:h-10 shrink-0"
        >
          {createSession.isPending ? <Loader2 size={15} className="animate-spin" /> : <CalendarDays size={15} />}
          {t("booking-patient:actions.book_appointment", "Pedir cita online")}
        </Button>
      </div>

      {/* ðŸ‘¥ LISTADO DE TARJETAS DE PROFESIONALES */}
      <div className="bg-card/40 rounded-2xl border border-border/40 p-3 md:p-4">
        {listProfessionals.length > 0 ? (
          <PublicClinicTeamList
            professionals={listProfessionals}
            clinicId={clinic.id}
            clinicName={clinic.name}
          />
        ) : (
          <div className="text-center p-8 text-xs text-muted-foreground font-medium">
            {t("clinic:tabs.team_empty")}
          </div>
        )}
      </div>
    </div>
  );
}
